import { FileMetadata } from "plugins/DrawPerimeter/plugin-actions/methods/upload/FirePerimeterPluginActionMethodUpload"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useState, useCallback, useRef, useEffect } from "react"
import { Coordinate } from "@nova/protobuf"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDateFormat,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    getPointsFromFile,
    validatePolygonFileUpload,
} from "features/data-management/perimeters/files"
import { calculateArea, omitLikePoints, totalDistance } from "@nova/calculate-geospatial"
import { formatArea, formatBytes, formatDate, formatDistance } from "@nova/format"
import {
    addMultiplePolygonsToFirePerimeterPreview,
    removePolygonFromFirePerimeterPreviewAtIndex,
    updatePerimeterCanConfirm,
} from "plugins/DrawPerimeter/FirePerimeterSlice"
import { zoomToShowPoints } from "features/map/zoom/thunks"

export const useFirePerimeterMethodUploadFiles = () => {
    const dispatch = useAppDispatch()

    const processingErrorTimeout = useRef<NodeJS.Timeout>(undefined)
    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const workspaceAreaUnit = useAppSelector(selectWorkspaceAreaUnit)
    const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)
    const [hasProcessingError, setHasProcessingError] = useState(false)

    const flagProcessingError = useCallback(() => {
        clearTimeout(processingErrorTimeout.current)
        setHasProcessingError(true)
        processingErrorTimeout.current = setTimeout(() => setHasProcessingError(false), 4000)
    }, [])

    const [files, setFiles] = useState<FileMetadata[]>([])
    const handleNewItems = useCallback(
        async (files: File[]) => {
            const newPolygons: Coordinate[][] = []

            const filesWithMetadata = (
                await Promise.all(
                    files.map(async (file) => {
                        const [valid, error] = await validatePolygonFileUpload([file])
                        if (valid) {
                            clearTimeout(processingErrorTimeout.current)
                            setHasProcessingError(false)

                            const points = await getPointsFromFile(file)
                            const cleanedPoints = omitLikePoints(points)
                            const planarPoints = cleanedPoints.map((path) =>
                                path.map((coord) => [coord[0], coord[1]] as [number, number])
                            )
                            const polygons = planarPoints.map((path) => ({
                                coordinates: path.map((point) => ({
                                    longitude: point[0],
                                    latitude: point[1],
                                })),
                            }))

                            // Whether any polygon in this file is an unclosed polygon.
                            const hasLine = polygons.reduce((hasLine, polygon) => {
                                const isLine =
                                    polygon.coordinates.at(0)?.longitude !==
                                        polygon.coordinates.at(-1)?.longitude &&
                                    polygon.coordinates.at(0)?.latitude !==
                                        polygon.coordinates.at(-1)?.latitude

                                if (isLine) {
                                    hasLine = true
                                    // Close the polygon.
                                    if (polygon.coordinates.length > 0)
                                        polygon.coordinates = [
                                            ...polygon.coordinates,
                                            polygon.coordinates[0],
                                        ]
                                }
                                return hasLine
                            }, false)

                            newPolygons.push(...polygons.map((p) => p.coordinates))

                            return {
                                file: file,
                                name: file.name,
                                polygons,
                                hasLine,
                                length: formatDistance({
                                    value: polygons.reduce((distance, polygon) => {
                                        return distance + totalDistance(polygon.coordinates, true)
                                    }, 0),
                                    unit: workspaceDistanceUnit,
                                }),
                                area: formatArea({
                                    value: polygons.reduce((area, polygon) => {
                                        return area + calculateArea(polygon.coordinates)
                                    }, 0),
                                    unit: workspaceAreaUnit,
                                }),
                                size: formatBytes(file.size, true),
                                dateCreated: formatDate({
                                    value: file.lastModified,
                                    format: workspaceDateFormat,
                                }),
                            } satisfies FileMetadata
                        } else {
                            flagProcessingError()
                            console.error("failed", error)
                        }
                    })
                )
            )
                .filter((file) => file !== undefined)
                .filter(
                    (file) =>
                        file.polygons.length >= 1 &&
                        file.polygons.every((polygon) => polygon.coordinates.length >= 4)
                )

            setFiles((prev) => [...prev, ...filesWithMetadata])

            const polygons = filesWithMetadata.flatMap((file) =>
                file.polygons.map((p) => p.coordinates)
            )
            dispatch(addMultiplePolygonsToFirePerimeterPreview(polygons))

            dispatch(
                zoomToShowPoints({
                    points: polygons.flatMap((polygon) =>
                        polygon.map(
                            ({ longitude, latitude }) => [longitude, latitude] as [number, number]
                        )
                    ),
                })
            )
        },
        [
            dispatch,
            flagProcessingError,
            workspaceAreaUnit,
            workspaceDateFormat,
            workspaceDistanceUnit,
        ]
    )

    const deleteFileAtIndex = useCallback(
        (index: number) => {
            setFiles((prev) => {
                prev.splice(index, 1)
                dispatch(removePolygonFromFirePerimeterPreviewAtIndex(index))
                return [...prev]
            })
        },
        [dispatch]
    )
    const hasFiles = files.length > 0

    useEffect(() => {
        dispatch(updatePerimeterCanConfirm(hasFiles))
    }, [hasFiles, dispatch])

    return { hasProcessingError, deleteFileAtIndex, handleNewItems, hasFiles, files }
}
